import VectorSource from "ol/source/Vector";
import { Feature, Map as olMap } from "ol";
import { LoggerService } from "src/app/services/logger.service";
import { Geometry, Point } from "ol/geom";
import MapConstants from "../constants";
import {
  Blast,
  BlastHolesDTO,
  HoleLegendProperties,
  HoleService,
  HoleStatus,
  HoleStatusHistoryUtil,
} from "src/app/services/hole.service";
import { ItemSortHelper } from "src/app/utils/itemSortHelper";
import { transform } from "ol/proj";
import WebGLPointsLayer from "ol/layer/WebGLPoints";
import { BehaviorSubject } from "rxjs";
import VectorLayer from "ol/layer/Vector";
import { Style, Icon, Circle, Fill, Stroke, Text } from "ol/style";
import { FeatureLike } from "ol/Feature";
import { WebGLVector } from "ol/layer";
import { SiteHoleTolerance, getThreshold } from "src/app/bo/site-hole-tolerance";

export interface DesignHoleData {
  holeName: string;
  head: number;
  length: number;
  toe: number;
  date: string;
}

export interface LoadingHoleData {
  holeName: string;
  totalBulk: number;
  steaming: number;
  linearQt: number;
  date: string;
}

export interface DeliveryHoleData {
  holeName: string;
  deliveryDate: Date;
  operatorName: string;
  deliveryId?: string;
}

export interface DesignQualityFilter {
  selectedMetric: string;
  holeDataList: DesignHoleData[];
  tolerance: SiteHoleTolerance;
}

export interface DesignDateFilter {
  selectedDates: string[];
  holeDataList: DesignHoleData[];
  allDates: string[];
}

export interface LoadingQualityFilter {
  selectedMetric: string;
  holeDataList: LoadingHoleData[];
  tolerance: SiteHoleTolerance;
}

export interface LoadingDateFilter {
  selectedDates: string[];
  holeDataList: LoadingHoleData[];
  allDates: string[];
}

export interface LoadingDeliveryFilter {
  selectedDeliveries: string[];
  deliveryDataList: DeliveryHoleData[];
  allDeliveries: DeliveryHoleData[];
}

export class HoleLayer {
  //private source :VectorSource;
  //private vectorLayer : VectorLayer<VectorSource>;
  private readonly source: VectorSource;
  private readonly vectorLayer: VectorLayer<VectorSource>;
  private readonly graySource: VectorSource; // Source pour les petits points gris
  private readonly grayLayer: WebGLVector<VectorSource>; // Layer pour les petits points gris
  private readonly map: olMap;

  private static readonly colorForKeyCache = new Map<string, string>();

  private readonly holeService: HoleService;
  private readonly logger: LoggerService;

  private currentBlastInView: number[] = [];
  //private cachedBlasts: Blast[] = [];
  private readonly cachedBlastIds: number[] = []; //equivalent to cachedBlasts.select(b => b.id).ToList();
  private readonly cachedHoleFeature = new Map<number, Feature<Geometry>[]>();

  private readonly holeLegendSubject = new BehaviorSubject<
    HoleLegendProperties[]
  >([]);
  public holeLegend$ = this.holeLegendSubject.asObservable();

  private listeTirsSelectionnes: number[] = [];
  private previousListeTirsSelectionnes: number[] = []; // Pour traquer les changements
  private holeLegendProperties: HoleLegendProperties[] = [];
    private currentFeatureFilter = 'all'; // Filtre de features actuel

  private qualityFilterInfo: DesignQualityFilter | null = null;
  private loadingQualityFilterInfo: LoadingQualityFilter | null = null;

  private isQualityFilterActive = false;
  private isLoadingQualityFilterActive = false;

  private dateFilterInfo: DesignDateFilter | null = null;
  private loadingDateFilterInfo: LoadingDateFilter | null = null;
  private loadingDeliveryFilterInfo: LoadingDeliveryFilter | null = null;

  private isDateFilterActive = false;
  private isLoadingDateFilterActive = false;
  private isLoadingDeliveryFilterActive = false;

  // Propriété pour le hole sélectionné
  private selectedHoleName: string | null = null;

  // Propriété pour contrôler l'affichage du nom des trous
  private showHoleNames = false;

  constructor(
    map: olMap,
    holeService: HoleService,
    logger: LoggerService,
    zIndex: number
  ) {
    this.holeService = holeService;
    this.logger = logger;
    this.map = map;

    // Couche principale pour les trous normaux
    this.source = new VectorSource();
    this.vectorLayer = new VectorLayer({
      source: this.source,
      style: (feature) => this.getHoleIconStyle(feature), // Style dynamique basé sur le hole
    });

    // Couche séparée pour les petits points gris (optimisée pour les performances)
    this.graySource = new VectorSource();
    this.grayLayer = new WebGLVector({
      source: this.graySource,
      style: {
        "circle-radius": 2,
        "circle-fill-color": "gray",
        "circle-stroke-color": "gray",
        "circle-stroke-width": 0.2,
        "circle-opacity": 0.6,
      },
    });

    this.map.addLayer(this.vectorLayer);
    this.map.addLayer(this.grayLayer);
    this.vectorLayer.setZIndex(zIndex);
    this.grayLayer.setZIndex(zIndex - 1); // Les points gris en arrière-plan
  }

  public static getColorForKey(dataKey: string): string {
    const cached = this.colorForKeyCache.get(dataKey);
    if (cached) {
      return cached;
    }

    const index = this.colorForKeyCache.size;
    const color = this.generateNewColor(index);

    this.colorForKeyCache.set(dataKey, color);
    return color;
  }

  private static generateNewColor(index: number): string {
    const predefinedColors = [
      '#FF6B6B',
      '#45B7D1',
      '#FFEAA7',
      '#DDA0DD',
      '#85C1E9',
      '#F8C471',
      '#82E0AA',
      '#F1948A',
      '#A3E4D7',
      '#D5A6BD'
    ];

    if (index < predefinedColors.length) {
      return predefinedColors[index];
    }

    const goldenRatio = 0.618033988749;
    const hue = (index * goldenRatio * 360) % 360;
    
    const saturation = 60 + (index % 3) * 15;
    const lightness = 45 + ((index % 4) * 10);
    
    return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
  }

  public clear() {
    this.source.clear();
    this.graySource.clear();
    this.currentBlastInView = [];
    this.cachedHoleFeature.clear();
    this.selectedHoleName = null;
    console.log("cleared holes");
  }

    // Obtenir tous les features de trous actuellement affichés
    public getAllHoleFeatures(): Feature<Geometry>[] {
        return this.source.getFeatures();
    }

  /**
   * Définit le hole sélectionné
   */
  public setSelectedHole(holeName: string | null) {
    this.selectedHoleName = holeName;
    console.log("HoleLayer: Selected hole:", holeName);
  }

  /**
   * Active ou désactive l'affichage des noms des trous
   */
  public setShowHoleNames(show: boolean) {
    this.showHoleNames = show;
    this.vectorLayer.changed();
  }

  /**
   * Rafraîchit les styles de tous les holes affichés
   */
  public refreshHoleStyles() {
    this.vectorLayer.changed();
  }

  public setSelectedBlast(newBlastIdsInView: number[]) {
    console.log("setSelectedBlast called with:", newBlastIdsInView);

    this.holeLegendSubject.next([]);
    this.holeLegendProperties = [];

    // Vider la source avant d'ajouter les nouvelles features
    this.source.clear();

    // Ajouter seulement les trous des tirs sélectionnés qui sont en cache
    this.cachedHoleFeature.forEach((features, blastId) => {
      if (newBlastIdsInView.includes(blastId)) {
        console.log(`Adding ${features.length} holes for blast ${blastId}`);
        features.forEach((f) => this.source.addFeature(f));
      }
    });

    // Forcer le rafraîchissement du layer
    this.vectorLayer.changed();
  }

  public setQualityFilter(qualityInfo: DesignQualityFilter | null) {
    this.qualityFilterInfo = qualityInfo;
    this.isQualityFilterActive = qualityInfo !== null;
    this.vectorLayer.changed();
  }

  public setLoadingQualityFilter(qualityInfo: LoadingQualityFilter | null) {
    this.loadingQualityFilterInfo = qualityInfo;
    this.isLoadingQualityFilterActive = qualityInfo !== null;
    this.vectorLayer.changed();
  }

  public setDateFilter(dateInfo: DesignDateFilter | null) {
    this.dateFilterInfo = dateInfo;
    this.isDateFilterActive = dateInfo !== null;
    this.vectorLayer.changed();
  }

  public setLoadingDateFilter(dateInfo: LoadingDateFilter | null) {
    this.loadingDateFilterInfo = dateInfo;
    this.isLoadingDateFilterActive = dateInfo !== null;
    this.vectorLayer.changed();
  }

  public setLoadingDeliveryFilter(deliveryInfo: LoadingDeliveryFilter | null) {
    this.loadingDeliveryFilterInfo = deliveryInfo;
    this.isLoadingDeliveryFilterActive = deliveryInfo !== null;
    this.vectorLayer.changed();
  }

  // Mapping de Hole vers HoleDTO
  private mapHoleToHoleDTO(holes: any[]): any[] {
    return holes.map(hole => ({
      publicGuid: hole.publicGuid,
      name: hole.name,
      plannedGeometry: hole.Geometry,
      measuredGeometry: hole.Geometry,
      status: hole.Status,
      positionLGZ: hole.PositionLGZ,
      PositionPlannedXYZ: hole.PositionPlannedXYZ,
      PositionMeasuredXYZ: hole.PositionMeasuredXYZ,
      statusHistory: hole.statusHistory,
      hasWater: hole.hasWater,
      hasTube: hole.hasTube,
      hasDeviation: hole.calculatedValues?.headPositionMagnitude != null || hole.calculatedValues?.toePositionMagnitude != null || false
    }));
  }

  // Mapping de HoleLoad vers HoleDTO
  private mapHoleLoadToHoleDTO(holeLoads: any[]): any[] {
    return holeLoads.map(holeLoad => ({
      publicGuid: holeLoad.holeId,
      name: holeLoad.name,
      plannedGeometry: null,
      measuredGeometry: null,
      status: holeLoad.status || this.getStatusFromHistory(holeLoad.statusHistory),
      positionLGZ: holeLoad.positionLGZ,
      PositionPlannedXYZ: holeLoad.holePosition,
      PositionMeasuredXYZ: holeLoad.holePosition,
      statusHistory: holeLoad.statusHistory,
      hasWater: holeLoad.hasWater,
      hasTube: false,
      hasDeviation: false
    }));
  }

  // Utilitaire pour déduire le statut actuel depuis l'historique (statut le plus avancé)
  // Ordre de priorité : du statut le plus avancé au moins avancé
  // HoleStatusField: Blasted=1, Drilled=2, Collapsed=4, Loaded=8, Loading=16, Planned=32, Missing=64
  private getStatusFromHistory(statusHistory: number): string {
    if (statusHistory & 1) return 'Blasted';
    if (statusHistory & 8) return 'Loaded';
    if (statusHistory & 16) return 'Loading';
    if (statusHistory & 4) return 'Collapsed';
    if (statusHistory & 2) return 'Drilled';
    if (statusHistory & 64) return 'Missing';
    if (statusHistory & 32) return 'Planned';
    return 'Unset';
  }

  public refreshHoles(
    newBlastIdsInView: number[],
    showHoles: boolean,
    listeTirsSelectionnes: number[],
    visibleHoleStatuses?: Set<HoleStatus>,
    filterStatus?: HoleStatus,
    featureFilter?: string,
    blast: Blast | null = null,
    isMapLoading$?: BehaviorSubject<boolean>,
    isFromDesing?: boolean,
    isFromLoad?: boolean
  ) {
    console.log("HoleLayer: refreshHoles called with showHoles:", showHoles);
    this.listeTirsSelectionnes = listeTirsSelectionnes;

    if (showHoles == false) {
      console.log("HoleLayer: showHoles is false, hiding all holes");
      this.vectorLayer.setVisible(false);
      this.grayLayer.setVisible(false);
      // Vider aussi la source pour éviter que les holes filtrés restent visibles
      this.hideAllHoles();
      return;
    }
    console.log("HoleLayer: showHoles is true, making layers visible");
    this.vectorLayer.setVisible(true);
    this.grayLayer.setVisible(true);

    //get holes not already in cache
    let missingIds = ItemSortHelper.arrayDifference(
      newBlastIdsInView,
      this.cachedBlastIds
    );

    if (
      missingIds.length == 0 &&
      this.listeTirsSelectionnes.length > 0 &&
      this.holeLegendProperties.length == 0
    ) {
      this.legendForVisibleCacheHole(this.holeLegendProperties);
    }

    //for performance reasons, we only refresh the holes if the blastIds are differents
    const blastIdsChanged = !ItemSortHelper.arraysHaveSameValues(
      newBlastIdsInView,
      this.currentBlastInView
    );
    const selectedBlastsChanged = !ItemSortHelper.arraysHaveSameValues(
      listeTirsSelectionnes,
      this.previousListeTirsSelectionnes
    );

    if (!blastIdsChanged && !selectedBlastsChanged) {
      console.log(
        "same blastIds and same selection, but checking if hole status visibility changed"
      );
      // Si les blast IDs ET la sélection sont identiques, mais qu'on a des filtres de statuts,
      // on rafraîchit quand même la visibilité
      if (visibleHoleStatuses) {
        this.refreshHoleVisibility(visibleHoleStatuses, filterStatus, featureFilter);
      } else {
        this.holeLegendSubject.next([...this.holeLegendProperties]);
      }
      return;
    }

    // Mise à jour des états
    this.currentBlastInView = newBlastIdsInView;
    this.previousListeTirsSelectionnes = [...listeTirsSelectionnes]; // Copie du tableau

    // Si la sélection a changé, on doit rafraîchir la visibilité même sans nouveaux blasts
    if (selectedBlastsChanged && !blastIdsChanged && missingIds.length === 0) {
      console.log("Selection changed, refreshing visibility from cache");
      if (visibleHoleStatuses) {
        this.refreshHoleVisibility(visibleHoleStatuses, filterStatus, featureFilter);
      } else {
        // Rafraîchir en utilisant le cache existant
        this.source.clear();
        this.holeLegendProperties = [];

        for (const [blastId, features] of this.cachedHoleFeature) {
          if (
            this.currentBlastInView.includes(blastId) &&
            this.listeTirsSelectionnes.includes(blastId)
          ) {
            for (const feature of features) {
              this.source.addFeature(feature);
              const hole = feature.get(MapConstants.HOLE_OBJECT);
              if (hole) {
                this.UpdateLegendForHoles(hole, this.holeLegendProperties);
                this.forceUpdateLegend();
              }
            }
          }
        }
        this.holeLegendSubject.next([...this.holeLegendProperties]);
      }
      return;
    }

    //TODO : create and cache features instead of fetching them from the server
    // then repopulate the source with the cached features when the blastIds are changed.
    
    if (blast !== null) {
      const holes = isFromDesing ? blast.BlastDesign || [] : blast.BlastLoading || [];
      const mappedHoles = isFromDesing ? this.mapHoleToHoleDTO(holes as any[]) : this.mapHoleLoadToHoleDTO(holes as any[]);
      
      const blasts: BlastHolesDTO[] = [{
        blastId: blast.tirId,
        blastName: blast.nom,
        holes: mappedHoles
      }];

      this.processBlastsHoles(blasts, visibleHoleStatuses, filterStatus, featureFilter, isMapLoading$);
    } else {
      this.holeService
        .GetBlastsHoles(missingIds)
        .then((blasts: BlastHolesDTO[]) => {
          this.processBlastsHoles(blasts, visibleHoleStatuses, filterStatus, featureFilter, isMapLoading$);
        })
        .finally(() => {
          console.log("finally");
          isMapLoading$.next(false);
        });
    }
  }

  private processBlastsHoles(
    blasts: BlastHolesDTO[],
    visibleHoleStatuses?: Set<HoleStatus>,
    filterStatus?: HoleStatus,
    featureFilter?: string,
    isMapLoading$?: BehaviorSubject<boolean>
  ) {
        for (var blast of blasts) {
          if (blast.holes.length > 0) {
            let featureList = [];
            let holesWithMissingCoordinates: string[] = [];

            for (var hole of blast.holes) {
              console.log(
                "HoleLayer: Processing hole",
                hole.name,
                "with statusHistory:",
                hole.statusHistory
              );
              if (hole.statusHistory !== undefined) {
                const analysis = HoleStatusHistoryUtil.analyzeStatusHistory(
                  hole.statusHistory
                );
                console.log(
                  "History analysis for hole",
                  hole.name,
                  ":",
                  analysis
                );
              } else {
                console.log(
                  "Hole",
                  hole.name,
                  "has no statusHistory, using current status:",
                  hole.status
                );
              }

              // Skip hole if position is not available or invalid
              if (
                !hole.positionLGZ ||
                hole.positionLGZ.longitude == null ||
                hole.positionLGZ.latitude == null ||
                !Number.isFinite(hole.positionLGZ.longitude) ||
                !Number.isFinite(hole.positionLGZ.latitude)
              ) {
                console.warn(
                  "Hole",
                  hole.name,
                  "has no valid position, skipping. positionLGZ:",
                  hole.positionLGZ
                );
                continue;
              }

              var coordinate = transform(
                [hole.positionLGZ.longitude, hole.positionLGZ.latitude],
                "EPSG:4326",
                MapConstants.EPSG_REFERENCE_OL
              );

              var holeFeature = new Feature({
                geometry: new Point(coordinate),
              });

              holeFeature.set(MapConstants.TIR_ID, blast.blastId);
              holeFeature.set(
                MapConstants.FEATURE_NAME,
                MapConstants.FEATURE_TYPE.hole
              );
              holeFeature.set(MapConstants.HOLE_OBJECT, hole);
              holeFeature.set(MapConstants.BLAST_NAME, blast.blastName);
              featureList.push(holeFeature);

              if (this.listeTirsSelectionnes.length > 0) {
                this.UpdateLegendForHoles(hole, this.holeLegendProperties);
              }
            }
            
            // Emit aggregated warning if any holes had missing coordinates
            if (holesWithMissingCoordinates.length > 0) {
              console.warn(
                `Blast ${blast.blastName} (ID: ${blast.blastId}): ${holesWithMissingCoordinates.length} hole(s) skipped due to missing coordinates: ${holesWithMissingCoordinates.join(', ')}`
              );
            }
            
            this.cachedHoleFeature.set(blast.blastId, featureList);
          }
        }

        this.cachedBlastIds.push(...blasts.map((b) => b.blastId));
        console.log("cachedBlasts:", this.cachedBlastIds);

        // Maintenant qu'on a tous les holes en cache (anciens + nouveaux),
        // rafraîchir la visibilité avec le filtre actuel
        if (visibleHoleStatuses) {
          this.refreshHoleVisibility(visibleHoleStatuses, filterStatus, featureFilter);
        } else {
          // Si pas de filtre, afficher uniquement les holes des tirs sélectionnés ET visibles
          this.source.clear();
          this.holeLegendProperties = [];

          for (const [blastId, features] of this.cachedHoleFeature) {
            if (
              this.currentBlastInView.includes(blastId) &&
              this.listeTirsSelectionnes.includes(blastId)
            ) {
              for (const feature of features) {
                this.source.addFeature(feature);
                const hole = feature.get(MapConstants.HOLE_OBJECT);
                if (hole) {
                  this.UpdateLegendForHoles(hole, this.holeLegendProperties);
                }
              }
            }
          }
          this.holeLegendSubject.next([...this.holeLegendProperties]);
        }

        if (isMapLoading$) {
          isMapLoading$.next(false);
        }
  }

  // Nouvelle méthode pour rafraîchir la visibilité des holes selon les statuts
  public refreshHoleVisibility(
    visibleHoleStatuses: Set<HoleStatus>,
    filterStatus?: HoleStatus, featureFilter?: string
  ) {
    console.log(
      "HoleLayer: refreshHoleVisibility called with statuses:",
      Array.from(visibleHoleStatuses)
    );
    
    // Stocker le filtre de features actuel
    this.currentFeatureFilter = featureFilter || 'all';

    // Vider seulement la source principale, pas la graySource
    this.source.clear();
    this.graySource.clear(); // Garder vide car on ne l'utilise plus pour le filtrage

    let holesProcessed = 0;
    let holesDisplayed = 0;

    // Collecter toutes les features
    const allFeatures: Feature[] = [];

    // Traiter tous les holes des tirs sélectionnés
    for (const [blastId, features] of this.cachedHoleFeature) {
        if (this.currentBlastInView.includes(blastId) && this.listeTirsSelectionnes.includes(blastId)) {
            for (const feature of features) {
                const hole = feature.get(MapConstants.HOLE_OBJECT);
                holesProcessed++;
                
                if (!hole) continue;
                
                // Cloner la feature et ajuster ses propriétés
                const displayFeature = feature.clone();
                
                // Vérifier le filtre - soit feature, soit statut, pas les deux
                let finalMatch = false;
                let matchingStatus: HoleStatus | null = null;
                
                // Si on a un filtre de feature, on l'utilise exclusivement
                if (featureFilter && featureFilter !== 'all') {
                    const hasWater = hole.hasWater || false;
                    const hasTube = hole.hasTube || false;
                    const hasDeviation = hole.hasDeviation || false;

                    console.log(`Hole ${hole.id || 'unknown'}: hasWater=${hasWater}, hasTube=${hasTube}, hasDeviation=${hasDeviation}, filter=${featureFilter}`);

                    switch (featureFilter) {
                        case 'water':
                            finalMatch = hasWater;
                            break;
                        case 'tube':
                            finalMatch = hasTube;
                            break;
                        case 'deviation':
                            finalMatch = hasDeviation;
                            break;
                        default:
                            finalMatch = true; // 'all'
                    }
                    
                    // Pour les filtres de features, utiliser le statut original du trou
                    if (finalMatch) {
                        matchingStatus = hole.status as HoleStatus;
                    }
                    
                    console.log(`Hole ${hole.id || 'unknown'}: finalMatch=${finalMatch}`);
                } else {
                    // Sinon, utiliser le filtre de statut normal
                    if (filterStatus === ("actual" as any)) {
                        // Filtre "actual" : utiliser le statut actuel du trou
                        finalMatch = visibleHoleStatuses.has(hole.status as HoleStatus);
                        if (finalMatch) {
                            matchingStatus = hole.status as HoleStatus;
                        }
                    } else if (hole.statusHistory !== undefined && hole.statusHistory !== 0) {
                        // Filtre spécifique : utiliser l'historique
                        finalMatch = HoleStatusHistoryUtil.hasHadAnyStatus(hole.statusHistory, visibleHoleStatuses);
                        if (finalMatch) {
                            matchingStatus = hole.status as HoleStatus;
                        }
                    } else {
                        finalMatch = visibleHoleStatuses.has(hole.status as HoleStatus);
                        if (finalMatch) {
                            matchingStatus = hole.status as HoleStatus;
                        }
                    }
                }
                
                if (finalMatch && matchingStatus) {
                    // Trou qui correspond aux filtres : utiliser l'icône du statut filtré
                    displayFeature.set('FILTERED_STATUS', filterStatus);
                    displayFeature.set('IS_FILTERED_MATCH', true);
                
                } else {
                    // Trou qui ne correspond pas : marquer comme gris
                    if (filterStatus?.toString() === 'actual') {
                        displayFeature.set('FILTERED_STATUS', hole.status);
                        displayFeature.set('IS_FILTERED_MATCH', filterStatus);
                    } else {
                        displayFeature.set('IS_FILTERED_MATCH', false);
                        displayFeature.set('FILTERED_STATUS', hole.status);
                    } // Garder le statut original mais il sera rendu en gris
                }
                
                allFeatures.push(displayFeature);
                holesDisplayed++;
            }
        }
    }

    // Ajouter toutes les features à la couche principale
    if (allFeatures.length > 0) {
      this.source.addFeatures(allFeatures);
    }

    console.log(
      `HoleLayer: Processed ${holesProcessed} holes, displayed ${holesDisplayed} holes`
    );

    // Mettre à jour la légende
    const mainCount = allFeatures.filter((f) =>
      f.get("IS_FILTERED_MATCH")
    ).length;
    const grayCount = allFeatures.filter(
      (f) => !f.get("IS_FILTERED_MATCH")
    ).length;
    this.updateLegendWithRatios(
      visibleHoleStatuses,
      filterStatus,
      mainCount,
      grayCount,
      holesProcessed
    );

    this.holeLegendSubject.next([...this.holeLegendProperties]);
  }

  // Méthode pour mettre à jour la légende avec les ratios en pourcentage
  private updateLegendWithRatios(
    visibleHoleStatuses: Set<HoleStatus>,
    filterStatus?: HoleStatus,
    mainCount?: number,
    grayCount?: number,
    totalCount?: number
  ) {
    this.holeLegendProperties = [];

    // Si le filtre est "actual", garder le comportement actuel (afficher tous les statuts sans pourcentage)
    if (filterStatus === ("actual" as any) || !filterStatus) {
      // Comportement actuel : afficher tous les statuts des trous visibles
      for (const [blastId, features] of this.cachedHoleFeature) {
        if (
          this.currentBlastInView.includes(blastId) &&
          this.listeTirsSelectionnes.includes(blastId)
        ) {
          for (const feature of features) {
            const hole = feature.get(MapConstants.HOLE_OBJECT);
            if (!hole) continue;

            let matchesFilter = false;
            if (hole.statusHistory !== undefined) {
              matchesFilter = HoleStatusHistoryUtil.hasHadAnyStatus(
                hole.statusHistory,
                visibleHoleStatuses
              );
            } else if (hole.identity && hole.identity.HoleStatusHistory) {
              matchesFilter = HoleStatusHistoryUtil.hasHadAnyStatus(
                hole.identity.HoleStatusHistory,
                visibleHoleStatuses
              );
            } else {
              matchesFilter = visibleHoleStatuses.has(
                hole.status as HoleStatus
              );
            }

            if (matchesFilter) {
              this.UpdateLegendForHoles(hole, this.holeLegendProperties);
            }
          }
        }
      }
    } else {
      // Nouveau comportement : afficher seulement le statut filtré avec les pourcentages
      if (
        mainCount !== undefined &&
        grayCount !== undefined &&
        totalCount !== undefined &&
        totalCount > 0
      ) {
        const mainPercentage = Math.round((mainCount / totalCount) * 100);
        const grayPercentage = Math.round((grayCount / totalCount) * 100);

        // Ajouter l'entrée pour le statut filtré (trous principaux)
        if (mainCount > 0) {
          this.holeLegendProperties.push({
            holeStatus: filterStatus as HoleStatus,
            numberOfHoles: mainCount,
            percentage: mainPercentage,
            totalHoles: totalCount,
          });
        }

        // Ajouter l'entrée pour les autres trous (points gris)
        if (grayCount > 0) {
          this.holeLegendProperties.push({
            holeStatus: "Other" as HoleStatus,
            numberOfHoles: grayCount,
            percentage: grayPercentage,
            totalHoles: totalCount,
          });
        }
      }
    }
  }

  // Méthode pour cacher tous les holes
  public hideAllHoles() {
    console.log(
      "HoleLayer: hideAllHoles called - clearing both sources and hiding layers"
    );
    this.source.clear();
    this.graySource.clear();
    this.vectorLayer.setVisible(false);
    this.grayLayer.setVisible(false);
    this.holeLegendProperties = [];
    this.holeLegendSubject.next([]);
  }

  private legendForVisibleCacheHole(
    holeLegendProperties: HoleLegendProperties[]
  ) {
    const filterHoleFeature = new Map(
      this.listeTirsSelectionnes
        .map((id) => [id, this.cachedHoleFeature.get(id)] as const)
        .filter(([, features]) => features !== undefined)
    );

    for (const [blastId, features] of filterHoleFeature) {
      for (let feature of features) {
        const hole = feature.get(MapConstants.HOLE_OBJECT);
        if (hole) {
          this.UpdateLegendForHoles(hole, holeLegendProperties);
        }
      }
    }
  }

  private UpdateLegendForHoles(
    hole: any,
    holeLegendProperties: HoleLegendProperties[]
  ) {
    const existing = holeLegendProperties.find(
      (hp) => hp.holeStatus === hole.status
    );
    if (existing) {
      existing.numberOfHoles++;
    } else {
      holeLegendProperties.push({
        holeStatus: hole.status as HoleStatus,
        numberOfHoles: 1,
      });
    }
  }
  private forceUpdateLegend() {
    this.holeLegendProperties = [];

    // Parcourir tous les features visibles pour reconstruire la légende
    this.source.forEachFeature((feature) => {
      const hole = feature.get(MapConstants.HOLE_OBJECT);
      if (hole) {
        this.UpdateLegendForHoles(hole, this.holeLegendProperties);
      }
    });

    this.holeLegendSubject.next([...this.holeLegendProperties]);
  }

  private getHoleIconStyle(feature: FeatureLike): Style | Style[] {
    const hole = feature.get(MapConstants.HOLE_OBJECT);
    if (!hole) {
      return this.getDefaultHoleIconStyle();
    }

    const isSelected = this.selectedHoleName === hole.name;

    if (this.isQualityFilterActive && this.qualityFilterInfo) {
      const baseStyle = this.getQualityIconStyle(hole);

      if (isSelected) {
        return this.showHoleNames
          ? this.createCompositeStyleWithText(
              baseStyle,
              isSelected,
              1.5,
              hole.name
            )
          : this.createCompositeStyle(baseStyle, isSelected, 1.5);
      }
      return this.showHoleNames
        ? this.createStyleWithText(baseStyle, hole.name)
        : baseStyle;
    }

    if (this.isLoadingQualityFilterActive && this.loadingQualityFilterInfo) {
      const baseStyle = this.getLoadingQualityColorStyle(hole);

      if (isSelected) {
        return this.showHoleNames
          ? this.createCompositeStyleWithText(
              baseStyle,
              isSelected,
              1.5,
              hole.name
            )
          : this.createCompositeStyle(baseStyle, isSelected, 1.5);
      }
      return this.showHoleNames
        ? this.createStyleWithText(baseStyle, hole.name)
        : baseStyle;
    }

    if (this.isLoadingDateFilterActive && this.loadingDateFilterInfo) {
      const dateStyle = this.getLoadingDateCircleStyle(hole);
      if (!dateStyle) {
        const grayStyle = new Style({
          image: new Icon({
            src: "assets/icon/hole-gray.svg",
            scale: 0.8,
            anchor: [0.5, 0.5],
            opacity: 0.6,
          }),
          zIndex: isSelected ? 1000 : 100,
        });

        if (isSelected) {
          return this.showHoleNames
            ? this.createCompositeStyleWithText(
                grayStyle,
                isSelected,
                1.3,
                hole.name
              )
            : this.createCompositeStyle(grayStyle, isSelected, 1.3);
        }
        return this.showHoleNames
          ? this.createStyleWithText(grayStyle, hole.name)
          : grayStyle;
      } else {
        if (isSelected) {
          return this.showHoleNames
            ? this.createCompositeStyleWithText(
                dateStyle,
                isSelected,
                1.3,
                hole.name
              )
            : this.createCompositeStyle(dateStyle, isSelected, 1.3);
        }
        return this.showHoleNames
          ? this.createStyleWithText(dateStyle, hole.name)
          : dateStyle;
      }
    }

    if (this.isLoadingDeliveryFilterActive && this.loadingDeliveryFilterInfo) {
      const deliveryStyle = this.getLoadingDeliveryCircleStyle(hole);
      if (!deliveryStyle) {
        const grayStyle = new Style({
          image: new Icon({
            src: "assets/icon/hole-gray.svg",
            scale: 0.8,
            anchor: [0.5, 0.5],
            opacity: 0.6,
          }),
          zIndex: isSelected ? 1000 : 100,
        });

        if (isSelected) {
          return this.showHoleNames
            ? this.createCompositeStyleWithText(
                grayStyle,
                isSelected,
                1.3,
                hole.name
              )
            : this.createCompositeStyle(grayStyle, isSelected, 1.3);
        }
        return this.showHoleNames
          ? this.createStyleWithText(grayStyle, hole.name)
          : grayStyle;
      } else {
        if (isSelected) {
          return this.showHoleNames
            ? this.createCompositeStyleWithText(
                deliveryStyle,
                isSelected,
                1.3,
                hole.name
              )
            : this.createCompositeStyle(deliveryStyle, isSelected, 1.3);
        }
        return this.showHoleNames
          ? this.createStyleWithText(deliveryStyle, hole.name)
          : deliveryStyle;
      }
    }

    if (this.isDateFilterActive && this.dateFilterInfo) {
      const dateStyle = this.getDateCircleStyle(hole);
      if (!dateStyle) {
        const grayStyle = new Style({
          image: new Icon({
            src: "assets/icon/hole-gray.svg",
            scale: 0.8,
            anchor: [0.5, 0.5],
            opacity: 0.6,
          }),
          zIndex: isSelected ? 1000 : 100,
        });

        if (isSelected) {
          return this.showHoleNames
            ? this.createCompositeStyleWithText(
                grayStyle,
                isSelected,
                1.3,
                hole.name
              )
            : this.createCompositeStyle(grayStyle, isSelected, 1.3);
        }
        return this.showHoleNames
          ? this.createStyleWithText(grayStyle, hole.name)
          : grayStyle;
      } else {
        if (isSelected) {
          return this.showHoleNames
            ? this.createCompositeStyleWithText(
                dateStyle,
                isSelected,
                1.3,
                hole.name
              )
            : this.createCompositeStyle(dateStyle, isSelected, 1.3);
        }
        return this.showHoleNames
          ? this.createStyleWithText(dateStyle, hole.name)
          : dateStyle;
      }
    }

    const isFilteredMatch = feature.get("IS_FILTERED_MATCH");
    const filteredStatus = feature.get("FILTERED_STATUS");
    const statusToUse = filteredStatus || hole.status;

    let iconPath = this.getHoleIconPath(statusToUse);
    let opacity = 1.0;
    
    // Si on a un filtre de features actif
    if (this.hasActiveFeatureFilter()) {
        if (isFilteredMatch === true) {
            // Trou qui correspond au filtre: utiliser l'icône spécifique (water/tube)
            iconPath = this.getHoleIconPathForFeatures(hole);
        } else {
            // Trou qui ne correspond pas: utiliser une icône grise et réduire l'opacité
            iconPath = this.getGrayHoleIconPath(statusToUse);
            opacity = 0.3;
        }
    } else {
        // Pas de filtre de features, utiliser la logique normale de statut
        if (isFilteredMatch === false && filteredStatus != 'actual') {
            // Si le trou ne correspond pas au filtre de statut, utiliser une version grise
            iconPath = this.getGrayHoleIconPath(statusToUse);
            opacity = 0.5;
        } else if (filteredStatus === 'actual' && isFilteredMatch === true) {
            iconPath = this.getHoleIconPath(hole.status);
        }
    }

    const baseStyle = new Style({
      image: new Icon({
        src: iconPath,
        scale: 0.8,
        anchor: [0.5, 0.5],
      }),
      zIndex: isSelected ? 1000 : 100,
    });

    if (isSelected) {
      return this.showHoleNames
        ? this.createCompositeStyleWithText(
            baseStyle,
            isSelected,
            1.3,
            hole.name
          )
        : this.createCompositeStyle(baseStyle, isSelected, 1.3);
    }

    return this.showHoleNames
      ? this.createStyleWithText(baseStyle, hole.name)
      : baseStyle;
  }

  /**
   * Crée un style avec texte à côté de l'icône
   */
  private createStyleWithText(baseStyle: Style, holeName: string): Style {
    return new Style({
      image: baseStyle.getImage(),
      text: new Text({
        text: holeName,
        font: "bold 12px sans-serif",
        fill: new Fill({
          color: "#000000",
        }),
        stroke: new Stroke({
          color: "#FFFFFF",
          width: 2,
        }),
        offsetX: 12,
        offsetY: 0,
        textAlign: "left",
        textBaseline: "middle",
      }),
      zIndex: baseStyle.getZIndex() || 100,
    });
  }

  /**
   * Crée un style composite avec l'icône de base, l'icône de sélection et le texte
   */
  private createCompositeStyleWithText(
    baseStyle: Style,
    isSelected: boolean,
    scale: number,
    holeName: string
  ): Style[] {
    if (!isSelected) {
      return [this.createStyleWithText(baseStyle, holeName)];
    }

    return [
      new Style({
        image: new Icon({
          src: "assets/icon/hole_selected.svg",
          scale: scale,
          anchor: [0.5, 0.5],
          offset: [0, 0],
        }),
        zIndex: 1000,
      }),
      new Style({
        image: baseStyle.getImage(),
        text: new Text({
          text: holeName,
          font: "bold 12px sans-serif",
          fill: new Fill({
            color: "#000000",
          }),
          stroke: new Stroke({
            color: "#FFFFFF",
            width: 2,
          }),
          offsetX: 12,
          offsetY: 0,
          textAlign: "left",
          textBaseline: "middle",
        }),
        zIndex: 1001,
      }),
    ];
  }

  /**
   * Crée un style composite avec l'icône de base et l'icône de sélection en dessous
   */
  private createCompositeStyle(
    baseStyle: Style,
    isSelected: boolean,
    scale: number
  ): Style[] {
    if (!isSelected) {
      return [baseStyle];
    }

    return [
      new Style({
        image: new Icon({
          src: "assets/icon/hole_selected.svg",
          scale: scale,
          anchor: [0.5, 0.5],
          offset: [0, 0],
        }),
        zIndex: 1000,
      }),
      new Style({
        image: baseStyle.getImage(),
        zIndex: 1001,
      }),
    ];
  }

  /**
   * Crée un style avec une icône selon la valeur de qualité
   */
  private getQualityIconStyle(hole: any): Style {
    const iconPath = this.getQualityIconPath(hole.name);
    const isSelected = this.selectedHoleName === hole.name;

    return new Style({
      image: new Icon({
        src: iconPath,
        scale: 0.8,
        anchor: [0.5, 0.5],
      }),
      zIndex: isSelected ? 1000 : 100,
    });
  }

  /**
   * Crée un style avec un cercle coloré selon la date du trou
   */
  private getDateCircleStyle(hole: any): Style | null {
    const color = this.getDateColorForHole(hole.name);

    if (!color) {
      return null;
    }

    const isSelected = this.selectedHoleName === hole.name;

    return new Style({
      image: new Circle({
        radius: 8,
        fill: new Fill({
          color: color,
        }),
        stroke: new Stroke({
          color: "#FFFFFF",
          width: 1,
        }),
      }),
      zIndex: isSelected ? 1000 : 100,
    });
  }

  /**
   * Calcule le chemin de l'icône selon la valeur de qualité du trou
   */
  private getQualityIconPath(holeName: string): string {
    const holeData = this.qualityFilterInfo.holeDataList.find(
      (h) => h.holeName === holeName
    );

    let value: number;
    let metricName: string;
    let good_val_config: number;
    let bad_val_config: number;
    const tolerance = this.qualityFilterInfo.tolerance;
    switch (this.qualityFilterInfo.selectedMetric) {
      case "HEAD":
        value = holeData?.head;
        metricName = "head";
        good_val_config = getThreshold(tolerance, 'HEAD')?.asDesignedMax ?? 0;
        bad_val_config = getThreshold(tolerance, 'HEAD')?.inToleranceMax ?? 0;
        break;
      case "LENGTH":
        value = holeData?.length;
        metricName = "length";
        good_val_config = getThreshold(tolerance, 'LENGTH')?.asDesignedMax ?? 0;
        bad_val_config = getThreshold(tolerance, 'LENGTH')?.inToleranceMax ?? 0;
        break;
      case "TOE":
        value = holeData?.toe;
        metricName = "toe";
        good_val_config = getThreshold(tolerance, 'TOE_POSITION')?.asDesignedMax ?? 0;
        bad_val_config = getThreshold(tolerance, 'TOE_POSITION')?.inToleranceMax ?? 0;
        break;
      default:
        return "assets/icon/hole-gray.svg";
    }
    // Déterminer la qualité
    if (value != undefined && value != null) {
      let qualityLevel: string;
      if (value < good_val_config) {
        qualityLevel = "good";
      } else if (value >= good_val_config && value <= bad_val_config) {
        qualityLevel = "middle";
      } else {
        qualityLevel = "bad";
      }
      const iconPath = `assets/icon/hole_${metricName}_${qualityLevel}.svg`;
      return iconPath;
    }
    const iconPath = `assets/icon/hole-gray.svg`;
    return iconPath;
  }

  /**
   * Crée un style avec un cercle coloré selon la qualité de loading du trou
   */
  private getLoadingQualityColorStyle(hole: any): Style {
    const color = this.getLoadingQualityColorForHole(hole.name);
    const isSelected = this.selectedHoleName === hole.name;
    
    if (!color || color === "") {
      return new Style({
        image: new Circle({
          radius: 4,
          fill: new Fill({
            color: "#acadaf",
          }),
          stroke: new Stroke({
            color: "#FFFFFF",
            width: 1,
          }),
        }),
        zIndex: isSelected ? 1000 : 100,
      });
    }
    
    return new Style({
      image: new Circle({
        radius: 8,
        fill: new Fill({
          color: color,
        }),
        stroke: new Stroke({
          color: "#FFFFFF",
          width: 1,
        }),
      }),
      zIndex: isSelected ? 1000 : 100,
    });
  }

  /**
   * Calcule la couleur selon la valeur de qualité du trou pour le loading
   */
  private getLoadingQualityColorForHole(holeName: string): string {
    const holeData = this.loadingQualityFilterInfo.holeDataList.find(
      (h) => h.holeName === holeName
    );

    if (!holeData) {
      return ""; // Gris par défaut si pas de données
    }

    let value: number;
    let good_val_config: number;
    let bad_val_config: number;

    const tolerance = this.loadingQualityFilterInfo.tolerance;
    switch (this.loadingQualityFilterInfo.selectedMetric) {
      case "BULK":
        value = holeData.totalBulk;
        good_val_config = getThreshold(tolerance, 'BULK')?.asDesignedMax ?? 0;
        bad_val_config = getThreshold(tolerance, 'BULK')?.inToleranceMax ?? 0;
        break;
      case "STREAMING_FINAL":
        value = holeData.steaming;
        good_val_config = getThreshold(tolerance, 'STEMMING')?.asDesignedMax ?? 0;
        bad_val_config = getThreshold(tolerance, 'STEMMING')?.inToleranceMax ?? 0;
        break;
      case "LINEAR_QUANTITY":
        value = holeData.linearQt;
        good_val_config = getThreshold(tolerance, 'LINEAR_QUANTITY')?.asDesignedMax ?? 0;
        bad_val_config = getThreshold(tolerance, 'LINEAR_QUANTITY')?.inToleranceMax ?? 0;
        break;
      default:
        return ""; // Gris par défaut
    }

    // Déterminer la couleur selon la qualité
    if (value !== undefined && value !== null) {
      if (value < good_val_config) {
        return "#2ecc71"; // Vert pour good
      } else if (value >= good_val_config && value <= bad_val_config) {
        return "#f1c40f"; // Jaune pour average
      } else {
        return "#e74c3c"; // Rouge pour bad
      }
    }

    return ""; // Gris par défaut si pas de valeur
  }

  /**
   * Crée un style avec un cercle coloré selon la date du trou pour le loading
   */
  private getLoadingDateCircleStyle(hole: any): Style | null {
    const color = this.getLoadingDateColorForHole(hole.name);

    if (!color) {
      return null;
    }

    const isSelected = this.selectedHoleName === hole.name;

    return new Style({
      image: new Circle({
        radius: 8,
        fill: new Fill({
          color: color,
        }),
        stroke: new Stroke({
          color: "#FFFFFF",
          width: 1,
        }),
      }),
      zIndex: isSelected ? 1000 : 100,
    });
  }

  /**
   * Calcule la couleur du cercle selon la date du trou pour le loading
   */
  private getLoadingDateColorForHole(holeName: string): string | null {
    if (
      !this.loadingDateFilterInfo ||
      !this.loadingDateFilterInfo.selectedDates ||
      this.loadingDateFilterInfo.selectedDates.length === 0
    ) {
      return null;
    }

    const holeData = this.loadingDateFilterInfo.holeDataList.find(
      (h) => h.holeName === holeName
    );
    if (!holeData) {
      return null;
    }

    if (
      !holeData ||
      !this.loadingDateFilterInfo.selectedDates.includes(holeData.date)
    ) {
      return null;
    }

    return HoleLayer.getColorForKey(holeData.date);
  }

  private getLoadingDeliveryCircleStyle(hole: any): Style | null {
    const color = this.getLoadingDeliveryColorForHole(hole.name);

    if (!color) {
      return null;
    }

    const isSelected = this.selectedHoleName === hole.name;

    return new Style({
      image: new Circle({
        radius: 8,
        fill: new Fill({
          color: color,
        }),
        stroke: new Stroke({
          color: "#FFFFFF",
          width: 1,
        }),
      }),
      zIndex: isSelected ? 1000 : 100,
    });
  }

  private getLoadingDeliveryColorForHole(holeName: string): string | null {
    if (
      !this.loadingDeliveryFilterInfo ||
      !this.loadingDeliveryFilterInfo.selectedDeliveries ||
      this.loadingDeliveryFilterInfo.selectedDeliveries.length === 0
    ) {
      return null;
    }

    // Trouver la delivery correspondant au trou
    const deliveryData = this.loadingDeliveryFilterInfo.deliveryDataList.find(
      (d) => d.holeName === holeName
    );
    if (!deliveryData) {
      return null;
    }

    const deliveryKey = `${deliveryData.operatorName}_${deliveryData.deliveryDate.toISOString()}`;
    if (!this.loadingDeliveryFilterInfo.selectedDeliveries.includes(deliveryKey)) {
      return null;
    }

    return HoleLayer.getColorForKey(deliveryKey);
  }

  /**
   * Calcule la couleur du cercle selon la date du trou
   */
  private getDateColorForHole(holeName: string): string | null {
    if (
      !this.dateFilterInfo ||
      !this.dateFilterInfo.selectedDates ||
      this.dateFilterInfo.selectedDates.length === 0
    ) {
      return null;
    }

    const holeData = this.dateFilterInfo.holeDataList.find(
      (h) => h.holeName === holeName
    );
    if (!holeData) {
      return null;
    }

    if (!this.dateFilterInfo.selectedDates.includes(holeData.date)) {
      return null;
    }

    return HoleLayer.getColorForKey(holeData.date);
  }

  private getGrayHoleIconPath(status: string): string {
    // Retourne des versions grises des icônes, ou une icône générique grise
    return "assets/icon/hole-gray.svg"; // Icône grise générique
  }

  public getHoleIconPath(status: string): string {
    // Retourne le chemin vers l'icône selon le statut
    switch (status) {
      case "Planned":
        return "assets/icon/hole-planned.svg";
      case "Drilled":
        return "assets/icon/hole-drilled.svg";
      case "Loading":
        return "assets/icon/hole-loading.svg";
      case "Loaded":
        return "assets/icon/hole-loaded.svg";
      case "Missing":
        return "assets/icon/hole-missing.svg";
      case "ToRedrill":
        return "assets/icon/hole-to-redrill.svg";
      case "Blasted":
        return "assets/icon/hole-blasted.svg";
      case "Collapsed":
        return "assets/icon/hole-collapsed.svg";
      case "ToAdjust":
        return "assets/icon/hole-to-adjust.svg";
      case "Redrilled":
        return "assets/icon/hole-redrilled.svg";
      case "Unset":
        return "assets/icon/hole-gray.svg";
      default:
        return "assets/icon/hole-gray.svg";
    }
  }

  private getDefaultHoleIconStyle(): Style {
    const baseStyle = new Style({
      image: new Icon({
        src: "assets/icon/hole-default.svg",
        scale: 0.8,
        anchor: [0.5, 0.5],
      }),
      zIndex: 100,
    });

    if (this.showHoleNames) {
      return new Style({
        image: baseStyle.getImage(),
        text: new Text({
          text: "Unknown",
          font: "bold 12px sans-serif",
          fill: new Fill({
            color: "#000000",
          }),
          stroke: new Stroke({
            color: "#FFFFFF",
            width: 2,
          }),
          offsetX: 20,
          offsetY: 0,
          textAlign: "left",
          textBaseline: "middle",
        }),
        zIndex: 100,
      });
    }

    return baseStyle;
  }

// Vérifier si un filtre de features est actif
private hasActiveFeatureFilter(): boolean {
    return this.currentFeatureFilter !== 'all';
}

// Obtenir l'icône selon les features du trou
private getHoleIconPathForFeatures(hole: any): string {
    const hasWater = hole.hasWater || false;
    const hasTube = hole.hasTube || false;
    const hasDeviation = hole.hasDeviation || false;
    
    // Si on filtre par water et que le trou a water, montrer l'icône water
    if (this.currentFeatureFilter === 'water' && hasWater) {
        return 'assets/icon/hole-water.svg';
    }
    // Si on filtre par tube et que le trou a tube, montrer l'icône tube  
    else if (this.currentFeatureFilter === 'tube' && hasTube) {
        return 'assets/icon/hole-tube.svg';
    }
    else if (this.currentFeatureFilter === 'deviation' && hole.hasDeviation) {
        return 'assets/icon/hole-deviation.svg';
    }
    
    // Fallback au statut normal pour tous les autres cas
    return this.getHoleIconPath(hole.status);
}
}
